import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../services/api';

/**
 * Forgot password — step 1. User enters email; we always show a generic
 * "if this email exists, a reset link is on its way" confirmation so we
 * don't leak whether an email is registered.
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error('Too many requests — please wait 15 minutes.');
      } else {
        toast.error('Could not send reset link. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-background min-h-screen flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md relative z-10">
        <Card className="border-border bg-card/95 backdrop-blur-sm">
          <CardContent className="pt-8 pb-6">
            {!sent ? (
              <>
                <h1 className="font-heading text-2xl font-bold mb-2">Forgot your password?</h1>
                <p className="text-sm text-muted-foreground mb-6">
                  Enter the email tied to your account and we'll send you a reset link.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="fp-email" className="uppercase text-xs tracking-wider">Email</Label>
                    <Input
                      id="fp-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@company.com"
                      className="input-industrial"
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 btn-volt btn-industrial"
                    disabled={loading || !email.trim()}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    {loading ? 'Sending...' : 'Send reset link'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="forgot-sent-confirmation">
                <div className="w-12 h-12 rounded-full bg-green-500/10 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Check your inbox</h2>
                <p className="text-sm text-muted-foreground mb-2">
                  If <span className="text-foreground font-medium">{email}</span> is registered,
                  we've sent a password reset link.
                </p>
                <p className="text-xs text-muted-foreground">
                  The link expires in 1 hour. Can't find it? Check your spam folder.
                </p>
              </div>
            )}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="forgot-back-link"
              >
                <ArrowLeft className="w-3 h-3" /> Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
